import { useEffect, useState } from 'react';
import { RULES } from '../content/rules';
import { Markdown } from '../components/Markdown';

/**
 * Página "Como Jogar". O conteúdo é estático (descreve o sistema do jogo, não
 * itens individuais) e está em src/content/rules.json, derivado e verificado
 * contra o código do jogo. Cartas e territórios, que mudam com frequência, vêm
 * do endpoint nas outras páginas.
 */
export function RulesPage(): React.JSX.Element {
  const ids = [...RULES.sections.map((s) => s.id), 'glossario'];
  const [active, setActive] = useState<string>(ids[0] ?? '');

  // Destaca no índice a seção visível. Um IntersectionObserver evita ficar
  // recalculando posições a cada evento de scroll.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: '-92px 0px -65% 0px' },
    );
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
    // `ids` é derivado de RULES, que é estático — observar uma vez basta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Rola até a seção sem tocar na URL: preventDefault evita que o navegador
   * siga o href="#secao" e empurre um hash na barra de endereço.
   */
  const goTo = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActive(id);
  };

  return (
    <>
      <div className="page-head">
        <h1>Como Jogar</h1>
        <p>As regras do jogo, explicadas do começo ao fim.</p>
      </div>

      <div className="rules-layout">
        <nav className="rules-toc" aria-label="Índice das regras">
          {RULES.sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={goTo(s.id)}
              className={active === s.id ? 'active' : undefined}
              aria-current={active === s.id ? 'true' : undefined}
            >
              {s.title}
            </a>
          ))}
          <a
            href="#glossario"
            onClick={goTo('glossario')}
            className={active === 'glossario' ? 'active' : undefined}
            aria-current={active === 'glossario' ? 'true' : undefined}
          >
            Glossário
          </a>
        </nav>

        <div className="rules-content">
          <Markdown source={RULES.intro} />

          {RULES.sections.map((section) => (
            <section key={section.id} id={section.id} className="rules-section">
              <h2>{section.title}</h2>
              <Markdown source={section.body} />
            </section>
          ))}

          <section id="glossario" className="rules-section">
            <h2>Glossário</h2>
            <dl className="glossary">
              {RULES.glossary.map((entry) => (
                <div className="term" key={entry.term}>
                  <dt>{entry.term}</dt>
                  <dd>{entry.definition}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      </div>
    </>
  );
}
